import type { InputHTMLAttributes, ReactNode } from 'react';
import {
  FieldError,
  Label,
  Input,
  Text,
  type TextFieldProps,
  TextArea,
} from 'react-aria-components';
import * as Styled from './TextField.styled';

interface FieldProps extends TextFieldProps {
  errorMessage?: string;
  hasWarning?: boolean;
  helpText?: ReactNode;
  /**
   * Escape hatch for attributes that must reach the rendered <input> itself.
   * `type`, `inputMode` and `pattern` passed as TextField props are consumed by
   * the ASCII validation check below and never forwarded, and `autoComplete` is
   * defaulted to 'on' by Styled.Field - so a field needing a specific input
   * purpose (WCAG 1.3.5) has no other way to express it. Opt-in: fields that
   * omit this are unaffected.
   */
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  isMultiline?: boolean;
  label: string;
  width?: number;
}

const regexDisallowedCharacters = /[^\p{L}\p{N} \-'\u0027\u2018\u2019\u02BC`.,/&()#:]/gu;

export const TextField = ({
  errorMessage,
  hasWarning = false,
  helpText,
  inputMode,
  inputProps,
  isMultiline = false,
  label,
  name,
  pattern,
  type,
  width,
  ...deferred
}: FieldProps) => {
  const asciiValidation =
    type === 'text' && !inputMode && !pattern
      ? {
          validate: (value: string) =>
            value && regexDisallowedCharacters.test(value)
              ? 'Please use standard letters, numbers, and symbols only.'
              : '',
        }
      : {};

  return (
    <Styled.Field
      $hasWarning={hasWarning}
      $width={width}
      data-validation-key={name}
      name={name}
      {...asciiValidation}
      {...deferred}
    >
      <Label>{label}</Label>
      <FieldError>{errorMessage}</FieldError>
      {isMultiline ? <TextArea /> : <Input {...inputProps} />}
      {helpText ? <Text slot="description">{helpText}</Text> : null}
    </Styled.Field>
  );
};
